import type { ShapeGeometry } from '../core/paint/shapes';
import { arrowHead } from '../core/paint/shapes';
import { loadImageUrl } from './imageIO';

/**
 * Drawing shapes into a layer.
 *
 * Shapes go through the same mask-then-composite path as the brush, so a shape
 * drawn at 40% opacity is a 40% shape rather than a pile of overlapping 40%
 * strokes, and so a shape is one history entry like everything else.
 */

export interface ShapeStyle {
  /** CSS colour for the outline. */
  color: string;
  /** 0..1 opacity of the whole shape. */
  opacity: number;
  /** Fill the interior of closed shapes instead of outlining them. */
  filled: boolean;
}

export function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: ShapeGeometry,
  style: ShapeStyle,
): void {
  const { bounds, from, to, strokeWidth, kind } = shape;
  ctx.save();
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = style.color;
  ctx.fillStyle = style.color;

  if (kind === 'line' || kind === 'arrow') {
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    if (kind === 'arrow') {
      const [left, right] = arrowHead(from, to, Math.max(6, strokeWidth * 3.5));
      ctx.beginPath();
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(left.x, left.y);
      ctx.lineTo(right.x, right.y);
      ctx.closePath();
      ctx.fill();
    }
  } else if (kind === 'rect') {
    if (style.filled) ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    else ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
  } else {
    const rx = Math.max(0.5, bounds.width / 2);
    const ry = Math.max(0.5, bounds.height / 2);
    ctx.beginPath();
    ctx.ellipse(
      bounds.x + bounds.width / 2,
      bounds.y + bounds.height / 2,
      rx,
      ry,
      0,
      0,
      Math.PI * 2,
    );
    if (style.filled) ctx.fill();
    else ctx.stroke();
  }

  ctx.restore();
}

export interface BakeShapeOptions {
  src: string | null;
  width: number;
  height: number;
  shape: ShapeGeometry;
  style: ShapeStyle;
}

/** Composite a finished shape into a layer and return the layer's new PNG. */
export async function bakeShape(options: BakeShapeOptions): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(options.width));
  canvas.height = Math.max(1, Math.round(options.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot render a canvas.');

  if (options.src) {
    const image = await loadImageUrl(options.src);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  }

  const mask = document.createElement('canvas');
  mask.width = canvas.width;
  mask.height = canvas.height;
  const maskCtx = mask.getContext('2d');
  if (!maskCtx) throw new Error('This browser cannot render a canvas.');
  drawShape(maskCtx, options.shape, { ...options.style, opacity: 1 });

  ctx.globalAlpha = Math.max(0, Math.min(1, options.style.opacity));
  ctx.drawImage(mask, 0, 0);
  return canvas.toDataURL('image/png');
}
